// src/stores/OrphanContentStore.ts

import { makeAutoObservable, runInAction } from 'mobx';
import { 
  OrphanContent, 
  ClaimRequest,
  EvidenceItem 
} from '@/types/marketplace';
import { orphanContentService } from '../services/orphanContentService';
import { RootStore } from '.';

export class OrphanContentStore {
      rootStore: RootStore;
  
  // Orphan content management
  orphanContents: OrphanContent[] = [];
  featuredOrphanContents: OrphanContent[] = [];
  
  // Claim requests
  claimRequests: ClaimRequest[] = [];
  myClaimRequests: ClaimRequest[] = [];
  
  // UI states
  isLoading: boolean = false;
  isSubmitting: boolean = false;
  error: string | null = null;
  
  // Pagination
  currentPage: number = 1;
  totalPages: number = 1;
  hasMore: boolean = true;
  
  // Filters
  filters = {
    contentType: '',
    potentialRevenueRange: { min: 0, max: 0 },
    sortBy: 'potentialRevenue' as 'potentialRevenue' | 'createdAt' | 'title'
  };

  constructor(rootStore: RootStore) {
    makeAutoObservable(this);
    this.rootStore = rootStore;
  }

  // Initialization
  async initialize() {
    this.setLoading(true);
    try {
      await Promise.all([
        this.loadOrphanContents(),
        this.loadFeaturedOrphanContents()
      ]);
    } catch (error) {
      this.setError('Không thể tải dữ liệu tác phẩm tìm tác giả');
      console.error('Orphan content initialization error:', error);
    } finally {
      this.setLoading(false);
    }
  }

  // Orphan content loading
  async loadOrphanContents(page: number = 1) {
    if (page === 1) {
      this.setLoading(true);
    }
    
    try {
      const response = await orphanContentService.getOrphanContents(page, 20, this.filters);
      
      runInAction(() => {
        if (page === 1) {
          this.orphanContents = response.data;
        } else {
          this.orphanContents.push(...response.data);
        }
        
        this.currentPage = page;
        this.totalPages = response.meta.pagination?.pageCount || 1;
        this.hasMore = page < this.totalPages;
      });
    } catch (error) {
      this.setError('Không thể tải danh sách tác phẩm tìm tác giả');
      console.error('Load orphan contents error:', error);
    } finally {
      if (page === 1) {
        this.setLoading(false);
      }
    }
  }

  async loadFeaturedOrphanContents() {
    try {
      const contents = await orphanContentService.getFeaturedOrphanContents();
      runInAction(() => {
        this.featuredOrphanContents = contents;
      });
    } catch (error) {
      console.error('Load featured orphan contents error:', error);
    }
  }

  async loadMoreOrphanContents() {
    if (!this.hasMore || this.isLoading) return;
    
    await this.loadOrphanContents(this.currentPage + 1);
  }

  async searchOrphanContents(query: string) {
    this.setLoading(true);
    this.setError(null);
    
    try {
      const contents = await orphanContentService.searchOrphanContents(query, this.filters);
      
      runInAction(() => {
        this.orphanContents = contents;
        this.currentPage = 1;
        this.hasMore = false; // Search results are typically paginated differently
      });
    } catch (error) {
      this.setError('Không thể tìm kiếm tác phẩm');
      console.error('Search orphan contents error:', error);
    } finally {
      this.setLoading(false);
    }
  }

  // Claim request management
  async submitClaimRequest(orphanContentId: string, claimData: {
    claimantInfo: {
      name: string;
      email: string;
      phone?: string;
      relationship: 'author' | 'heir' | 'publisher' | 'representative';
    };
    evidence: EvidenceItem[];
    statement: string;
  }) {
    this.setSubmitting(true);
    this.setError(null);
    
    try {
      const claimRequest = await orphanContentService.submitClaimRequest(orphanContentId, claimData);
      
      runInAction(() => {
        this.myClaimRequests.unshift(claimRequest);
        this.claimRequests.unshift(claimRequest);
        
        // Update the orphan content status
        const orphanContent = this.orphanContents.find(c => c.documentId === orphanContentId);
        if (orphanContent) {
          orphanContent.claimRequests.push(claimRequest);
        }
      });
      
      return claimRequest;
    } catch (error) {
      this.setError('Không thể gửi yêu cầu claim quyền sở hữu');
      console.error('Submit claim request error:', error);
      throw error;
    } finally {
      this.setSubmitting(false);
    }
  }

  async loadMyClaimRequests(userId: string) {
    try {
      const requests = await orphanContentService.getUserClaimRequests(userId);
      runInAction(() => {
        this.myClaimRequests = requests;
      });
    } catch (error) {
      console.error('Load my claim requests error:', error);
    }
  }

  async loadAllClaimRequests() {
    try {
      const requests = await orphanContentService.getAllClaimRequests();
      runInAction(() => {
        this.claimRequests = requests;
      });
    } catch (error) {
      console.error('Load all claim requests error:', error);
    }
  }

  async updateClaimRequest(requestId: string, updateData: {
    evidence?: EvidenceItem[];
    statement?: string;
  }) {
    this.setSubmitting(true);
    this.setError(null);
    
    try {
      const updatedRequest = await orphanContentService.updateClaimRequest(requestId, updateData);
      
      runInAction(() => {
        this.updateRequestInLists(updatedRequest);
      });
      
      return updatedRequest;
    } catch (error) {
      this.setError('Không thể cập nhật yêu cầu claim');
      console.error('Update claim request error:', error);
      throw error;
    } finally {
      this.setSubmitting(false);
    }
  }

  async withdrawClaimRequest(requestId: string) {
    this.setSubmitting(true);
    this.setError(null);
    
    try {
      await orphanContentService.withdrawClaimRequest(requestId);
      
      runInAction(() => {
        this.myClaimRequests = this.myClaimRequests.filter(r => r.documentId !== requestId);
        this.claimRequests = this.claimRequests.filter(r => r.documentId !== requestId);
      });
    } catch (error) {
      this.setError('Không thể rút lại yêu cầu claim');
      console.error('Withdraw claim request error:', error);
      throw error;
    } finally {
      this.setSubmitting(false);
    }
  }

  // Admin/Staff actions for claim requests
  async reviewClaimRequest(requestId: string, decision: 'approved' | 'rejected', reviewNotes?: string) {
    this.setSubmitting(true);
    this.setError(null);
    
    try {
      const reviewedRequest = await orphanContentService.reviewClaimRequest(requestId, decision, reviewNotes);
      
      runInAction(() => {
        this.updateRequestInLists(reviewedRequest);
        
        // If approved, update orphan content status
        if (decision === 'approved') {
          const orphanContent = this.orphanContents.find(c => 
            c.claimRequests.some(cr => cr.documentId === requestId)
          );
          if (orphanContent) {
            orphanContent.status = 'claimed';
          }
        }
      });
      
      return reviewedRequest;
    } catch (error) {
      this.setError('Không thể xem xét yêu cầu claim');
      console.error('Review claim request error:', error);
      throw error;
    } finally {
      this.setSubmitting(false);
    }
  }

  // Evidence management
  async uploadEvidence(claimRequestId: string, evidenceData: {
    type: 'document' | 'certificate' | 'publication' | 'witness' | 'other';
    title: string;
    description: string;
    file: File;
  }) {
    this.setSubmitting(true);
    this.setError(null);
    
    try {
      const uploadedEvidence = await orphanContentService.uploadEvidence(claimRequestId, evidenceData);
      
      runInAction(() => {
        const claimRequest = this.myClaimRequests.find(r => r.documentId === claimRequestId);
        if (claimRequest) {
          claimRequest.evidence.push(uploadedEvidence);
        }
      });
      
      return uploadedEvidence;
    } catch (error) {
      this.setError('Không thể tải lên bằng chứng');
      console.error('Upload evidence error:', error);
      throw error;
    } finally {
      this.setSubmitting(false);
    }
  }

  async deleteEvidence(claimRequestId: string, evidenceId: string) {
    this.setSubmitting(true);
    this.setError(null);
    
    try {
      await orphanContentService.deleteEvidence(claimRequestId, evidenceId);
      
      runInAction(() => {
        const claimRequest = this.myClaimRequests.find(r => r.documentId === claimRequestId);
        if (claimRequest) {
          claimRequest.evidence = claimRequest.evidence.filter(
            e => e.fileUrl !== evidenceId
          );
        }
      });
    } catch (error) {
      this.setError('Không thể xóa bằng chứng');
      console.error('Delete evidence error:', error);
      throw error;
    } finally {
      this.setSubmitting(false);
    }
  }

  // Filter management
  updateFilters(newFilters: Partial<typeof this.filters>) {
    this.filters = { ...this.filters, ...newFilters };
    this.loadOrphanContents(1); // Reload with new filters
  }

  clearFilters() {
    this.filters = {
      contentType: '',
      potentialRevenueRange: { min: 0, max: 0 },
      sortBy: 'potentialRevenue'
    };
    this.loadOrphanContents(1);
  }

  // Orphan content details
  async getOrphanContentDetails(contentId: string): Promise<OrphanContent | null> {
    try {
      return await orphanContentService.getOrphanContentById(contentId);
    } catch (error) {
      console.error('Get orphan content details error:', error);
      return null;
    }
  }

  // Statistics and analytics
  async getOrphanContentStats(): Promise<any> {
    try {
      return await orphanContentService.getOrphanContentStats();
    } catch (error) {
      console.error('Get orphan content stats error:', error);
      return null;
    }
  }

  async getClaimRequestStats(): Promise<any> {
    try {
      return await orphanContentService.getClaimRequestStats();
    } catch (error) {
      console.error('Get claim request stats error:', error);
      return null;
    }
  }

  // Helper methods
  private updateRequestInLists(updatedRequest: ClaimRequest) {
    // Update in my requests
    const myIndex = this.myClaimRequests.findIndex(r => r.documentId === updatedRequest.documentId);
    if (myIndex !== -1) {
      this.myClaimRequests[myIndex] = updatedRequest;
    }
    
    // Update in all requests
    const allIndex = this.claimRequests.findIndex(r => r.documentId === updatedRequest.documentId);
    if (allIndex !== -1) {
      this.claimRequests[allIndex] = updatedRequest;
    }
  }

  // State management
  setLoading(loading: boolean) {
    this.isLoading = loading;
  }

  setSubmitting(submitting: boolean) {
    this.isSubmitting = submitting;
  }

  setError(error: string | null) {
    this.error = error;
  }

  clearError() {
    this.error = null;
  }

  reset() {
    this.orphanContents = [];
    this.claimRequests = [];
    this.myClaimRequests = [];
    this.currentPage = 1;
    this.totalPages = 1;
    this.hasMore = true;
    this.error = null;
  }

  // Getters
  get hasOrphanContents() {
    return this.orphanContents.length > 0;
  }

  get totalOrphanContents() {
    return this.orphanContents.length;
  }

  get totalClaimRequests() {
    return this.myClaimRequests.length;
  }

  get pendingClaimRequests() {
    return this.myClaimRequests.filter(r => r.status === 'submitted');
  }

  get approvedClaimRequests() {
    return this.myClaimRequests.filter(r => r.status === 'approved');
  }

  get rejectedClaimRequests() {
    return this.myClaimRequests.filter(r => r.status === 'rejected');
  }

  get underReviewClaimRequests() {
    return this.myClaimRequests.filter(r => r.status === 'under_review');
  }

  get totalPotentialRevenue() {
    return this.orphanContents.reduce((sum, content) => 
      sum + content.potentialRevenue, 0
    );
  }

  get availableOrphanContents() {
    return this.orphanContents.filter(c => c.status === 'available');
  }

  get claimedOrphanContents() {
    return this.orphanContents.filter(c => c.status === 'claimed');
  }

  get disputedOrphanContents() {
    return this.orphanContents.filter(c => c.status === 'disputed');
  }

  // Analytics helpers
  getOrphanContentsByType() {
    const typeStats = this.orphanContents.reduce((acc, content) => {
      const type = content.contentType;
      acc[type] = (acc[type] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);
    
    return Object.entries(typeStats).map(([type, count]) => ({ type, count }));
  }

  getClaimRequestsByStatus() {
    const statusStats = this.myClaimRequests.reduce((acc, request) => {
      const status = request.status;
      acc[status] = (acc[status] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);
    
    return Object.entries(statusStats).map(([status, count]) => ({ status, count }));
  }

  getHighValueOrphanContents(threshold: number = 1000000) {
    return this.orphanContents.filter(c => c.potentialRevenue >= threshold);
  }

  getRecentlyAddedOrphanContents(days: number = 30) {
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - days);
    
    return this.orphanContents.filter(c => 
      new Date(c.createdAt) >= cutoffDate
    );
  }

  // Search and discovery helpers
  searchOrphanContentsByKeyword(keyword: string) {
    const lowerKeyword = keyword.toLowerCase();
    return this.orphanContents.filter(content =>
      content.title.toLowerCase().includes(lowerKeyword) ||
      content.description.toLowerCase().includes(lowerKeyword)
    );
  }

  getOrphanContentsByPotentialRevenue(min: number, max: number) {
    return this.orphanContents.filter(content =>
      content.potentialRevenue >= min &&
      content.potentialRevenue <= max
    );
  }

  getSimilarOrphanContents(contentId: string, limit: number = 5) {
    const targetContent = this.orphanContents.find(c => c.documentId === contentId);
    if (!targetContent) return [];
    
    return this.orphanContents
      .filter(c => 
        c.documentId !== contentId &&
        c.contentType === targetContent.contentType
      )
      .sort((a, b) => b.potentialRevenue - a.potentialRevenue)
      .slice(0, limit);
  }
}

export default OrphanContentStore;